import React, { useState } from 'react';
import { useUserProfile } from '../context/UserProfileContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE } from '../config';
import { ZODIAC_SIGNS, getZodiacSign, calculateLifePathNumber, getChineseZodiac } from '../data/astroData';

export default function Profile() {
  const { profile, updateProfile, savedReadings, deleteReading, favorites, removeFavorite, clearProfile } = useUserProfile();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState('');
  const [formData, setFormData] = useState({
    name: profile.name || '',
    dateOfBirth: profile.dateOfBirth || '',
    timeOfBirth: profile.timeOfBirth || '',
    placeOfBirth: profile.placeOfBirth || '',
  });

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSaveProfile = () => {
    const updates = { ...formData };

    // Calculate zodiac sign
    if (formData.dateOfBirth) {
      const [year, month, day] = formData.dateOfBirth.split('-').map(Number);
      const sign = getZodiacSign(month, day);
      updates.zodiacSign = sign?.name || '';
      updates.lifePathNumber = calculateLifePathNumber(formData.dateOfBirth);
      const chinese = getChineseZodiac(year);
      updates.chineseZodiac = chinese?.animal || '';
    }

    updateProfile(updates);
    setEditMode(false);
  };

  const handleClearProfile = () => {
    if (window.confirm(t('confirmClearProfile'))) {
      clearProfile();
      setFormData({ name: '', dateOfBirth: '', timeOfBirth: '', placeOfBirth: '' });
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action will deactivate your account and remove your data from active use.')) return;
    setDeleteLoading(true);
    setDeleteMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/auth/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error('Failed to delete account');
      setDeleteMsg('Your account has been scheduled for deletion. You will be logged out.');
      setTimeout(() => { logout(); }, 3000);
    } catch (err) {
      setDeleteMsg(err.message || 'Something went wrong. Please email support@astroa2z.com.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const zodiacSign = profile.zodiacSign ? ZODIAC_SIGNS.find(s => s.name === profile.zodiacSign) : null;

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h2>{t('profileTitle')}</h2>
          <p>{t('profileDesc')}</p>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {t('myProfile')}
          </button>
          <button
            className={`tab ${activeTab === 'readings' ? 'active' : ''}`}
            onClick={() => setActiveTab('readings')}
          >
            {t('savedReadings')} ({profile.savedReadings?.length || 0})
          </button>
          <button
            className={`tab ${activeTab === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            {t('favorites')} ({profile.favorites?.length || 0})
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="profile-section">
            {profile.zodiacSign && !editMode && (
              <div className="profile-summary">
                <div className="profile-zodiac">
                  <div className="zodiac-circle-large" style={{ background: zodiacSign?.color }}>
                    <span className="zodiac-symbol-large">{zodiacSign?.symbol}</span>
                  </div>
                  <h3>{profile.name || t('unnamed')}</h3>
                  <p className="zodiac-name">{profile.zodiacSign} • {zodiacSign?.rashi}</p>
                  {profile.lifePathNumber && (
                    <p className="life-path">{t('lifePathNumber')}: {profile.lifePathNumber}</p>
                  )}
                  {profile.chineseZodiac && (
                    <p className="chinese">{t('chineseZodiac')}: {profile.chineseZodiac}</p>
                  )}
                </div>
              </div>
            )}

            {(editMode || !profile.zodiacSign) && (
              <div className="form-section">
                <h3>{profile.zodiacSign ? t('editProfile') : t('setupProfile')}</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>{t('fullName')}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('enterFullName')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dateOfBirth')} *</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('timeOfBirth')}</label>
                    <input
                      type="time"
                      name="timeOfBirth"
                      value={formData.timeOfBirth}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('placeOfBirth')}</label>
                    <input
                      type="text"
                      name="placeOfBirth"
                      value={formData.placeOfBirth}
                      onChange={handleInputChange}
                      placeholder={t('cityCountry')}
                    />
                  </div>
                </div>
                <div className="button-group">
                  <button className="btn btn-primary" onClick={handleSaveProfile}>
                    {t('saveProfile')}
                  </button>
                  {editMode && (
                    <button className="btn btn-secondary" onClick={() => setEditMode(false)}>
                      {t('cancel')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {profile.zodiacSign && !editMode && (
              <div className="button-group" style={{ marginTop: '2rem' }}>
                <button className="btn btn-secondary" onClick={() => setEditMode(true)}>
                  {t('editProfile')}
                </button>
                <button className="btn btn-danger" onClick={handleClearProfile}>
                  {t('clearProfile')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Saved Readings Tab */}
        {activeTab === 'readings' && (
          <div className="readings-section">
            {profile.savedReadings?.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">📜</span>
                <p>{t('noSavedReadings')}</p>
              </div>
            ) : (
              <div className="readings-list">
                {profile.savedReadings?.map(reading => (
                  <div key={reading.id} className="reading-item">
                    <div className="reading-header">
                      <span className="reading-type">{reading.type}</span>
                      <span className="reading-date">
                        {new Date(reading.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="reading-preview">
                      {reading.content?.substring(0, 200)}...
                    </div>
                    <div className="reading-actions">
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => deleteReading(reading.id)}
                      >
                        {t('delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Favorites Tab */}
        {activeTab === 'favorites' && (
          <div className="favorites-section">
            {profile.favorites?.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">⭐</span>
                <p>{t('noFavorites')}</p>
              </div>
            ) : (
              <div className="favorites-list">
                {profile.favorites?.map(fav => (
                  <div key={fav.id} className="favorite-item">
                    <span className="favorite-type">{fav.type}</span>
                    <span className="favorite-title">{fav.title}</span>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => removeFavorite(fav.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Account Deletion Section */}
        {user && (
          <div style={{ marginTop: '3rem', padding: '1.5rem', border: '1px solid rgba(227,66,52,0.3)', borderRadius: '12px', background: 'rgba(227,66,52,0.05)' }}>
            <h3 style={{ color: '#e34234', marginBottom: '0.75rem' }}>⚠️ Delete My Account</h3>
            <p style={{ fontSize: '0.85rem', color: '#999', lineHeight: 1.7, marginBottom: '0.5rem' }}>
              Requesting account deletion will <strong>soft-delete</strong> your account. This means:
            </p>
            <ul style={{ fontSize: '0.82rem', color: '#999', lineHeight: 1.8, paddingLeft: '1.2rem', marginBottom: '1rem' }}>
              <li>Your account will be deactivated and hidden from all active services</li>
              <li>Your personal data will <strong>not</strong> be used for marketing, advertising, or any promotional purposes</li>
              <li>Data is retained in an inactive state for 30 days for recovery purposes, after which it is permanently purged</li>
              <li>Certain data may be retained as required by Indian tax and legal compliance obligations</li>
              <li>You may contact <a href="mailto:dpo@astroa2z.com" style={{ color: '#f9a825' }}>dpo@astroa2z.com</a> to request immediate permanent deletion</li>
            </ul>
            {deleteMsg && (
              <p style={{ fontSize: '0.85rem', color: deleteMsg.includes('scheduled') ? '#4caf50' : '#e34234', marginBottom: '1rem' }}>
                {deleteMsg}
              </p>
            )}
            {!deleteRequested ? (
              <button
                className="btn btn-danger"
                onClick={() => setDeleteRequested(true)}
                style={{ fontSize: '0.85rem' }}
              >
                Request Account Deletion
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                  style={{ fontSize: '0.85rem' }}
                >
                  {deleteLoading ? 'Processing...' : 'Yes, Delete My Account'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setDeleteRequested(false)}
                  style={{ fontSize: '0.85rem' }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
